/**
 * Which existing placement a new drop finishes, rather than sitting beside.
 *
 * EN and FR boxes are authored independently — the two PDFs are separate
 * uploads with different layouts — but they belong to ONE field. So a drop on
 * the French page has two possible meanings: a brand-new placement, or the
 * missing French half of one already placed in English. Guessing wrong is not
 * cosmetic: `WorkflowTemplate.EnsureSubmittable` checks each field's own
 * `enPlacement`/`frPlacement` pair rather than the role's totals, so two
 * single-language fields both fail the parity gate while looking correct on
 * screen — each renders fine in its own language's view.
 *
 * Extracted from the placement page's drop handler so the rule can be tested
 * without a PDF, a router and a viewport.
 */
import type { SignatureField } from '@/api/workflows';

/**
 * Index of the field this drop should complete, or `-1` for a new placement.
 *
 * FIFO over array order: the first still-incomplete field for this signatory is
 * the one being completed, which matches the order the boxes were placed in.
 *
 * Only the signature's own placement counts as "incomplete" — its companion date
 * and input boxes are authored per language from its control row, so a signature
 * dated in English and not in French is finished, not half-placed.
 */
export function indexOfFieldToComplete(
  fields: readonly SignatureField[],
  role: string,
  lang: 'en' | 'fr',
): number {
  const other = lang === 'fr' ? 'en' : 'fr';
  return fields.findIndex((f) => f.role === role && !placementOf(f, lang) && placementOf(f, other));
}

/**
 * What the size of a completing drop follows: the footprint of the field being
 * finished, NOT the signature default.
 *
 * The two halves of one field describe the same box in two documents, so the
 * second one has to match the first. Taking the default instead stored an
 * initials box (60 wide) at 150 on whichever language was authored second.
 */
export function completingBoxIsInitials(field: SignatureField): boolean {
  return field.isInitials ?? false;
}

function placementOf(field: SignatureField, lang: 'en' | 'fr') {
  return (lang === 'fr' ? field.frPlacement : field.enPlacement) ?? null;
}
